import { useAuth } from "../../AuthContext";
import "../../style/ExpandedSideBar.css";
import React, { useState, useEffect } from "react";

function ExpandedSideBar({ documents, setDocuments, onDocumentClick, setSelectedDocument, setIsEditing }) {
  const API_BASE_URL = process.env.REACT_APP_API_SOURCE;
  const { userId } = useAuth(); // Get logged-in user ID
  const [menuOpen, setMenuOpen] = useState(null); // Track which document has open menu
  const [renaming, setRenaming] = useState(null); // Track which document is being renamed
  const [newName, setNewName] = useState(""); // Store the new name
  const [starredDocs, setStarredDocs] = useState({}); // Track starred documents

  // Fetch documents for the logged-in user
  useEffect(() => {
    if (userId) {
      fetch(`${API_BASE_URL}/backend/api/Documents/fetchDocuments.php?userId=${userId}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setDocuments(data.documents);
            
            // Initialize starred status from localStorage
            const savedStars = localStorage.getItem(`starredDocs_${userId}`);
            if (savedStars) {
              setStarredDocs(JSON.parse(savedStars));
            }
          } else {
            console.error("Error fetching documents:", data.message);
          }
        })
        .catch((err) => console.error("Error fetching documents:", err));
    }
  }, [userId, setDocuments]);

  // Save starred documents to localStorage whenever they change
  useEffect(() => {
    if (userId && Object.keys(starredDocs).length > 0) {
      localStorage.setItem(`starredDocs_${userId}`, JSON.stringify(starredDocs));
    }
  }, [starredDocs, userId]);

  const handleAddDocument = async (event) => {
    const file = event.target.files[0];
    if (file && userId) {
      const formData = new FormData();
      formData.append("pdfFile", file);
      formData.append("userId", userId); // ✅ Send userId with request

      fetch(`${API_BASE_URL}/backend/api/Documents/documentUpload.php`, {
        method: "POST",
        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.status === "success") {
            fetch(`${API_BASE_URL}/backend/api/Documents/fetchDocuments.php?userId=${userId}`)
              .then((res) => res.json())
              .then((data) => {
                if (data.status === "success") {
                  setDocuments(data.documents);
                } else {
                  console.error("Error fetching documents:", data.message);
                }
              })
              .catch((err) => console.error("Error fetching documents:", err));
          } else {
            console.error("Error uploading document:", result.message);
          }
        })
        .catch((error) => {
          console.error("Error uploading document:", error); // Log the actual error
        });
    }
  };

  const handleDelete = async (event, documentId) => {
    event.stopPropagation();  // Prevent the click from propagating to the document's click handler
    setMenuOpen(null); // Close the menu
    
    try {
      // Send a POST request to delete the document
      const response = await fetch(`${API_BASE_URL}/backend/api/Documents/deleteDocument.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,  // Send the userId as part of the request
          name: documentId,  // Send the document name
        }),
      });
  
      const result = await response.json();  // Parse the JSON response
  
      if (result.status === "success") {
        // If the document is deleted successfully, update the state
        setDocuments((prevDocs) => prevDocs.filter(doc => doc.name !== documentId));

        // Close the iframe if the document being deleted was the one selected
        setSelectedDocument((prevSelectedDoc) =>
          prevSelectedDoc === documentId ? null : prevSelectedDoc
        );
        
        // Remove from starred documents if it was starred
        if (starredDocs[documentId]) {
          const newStarredDocs = {...starredDocs};
          delete newStarredDocs[documentId];
          setStarredDocs(newStarredDocs);
        }
      } else {
        console.error("Failed to delete document:", result.message);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const handleEdit = (event, documentName) => {
    event.stopPropagation(); // Prevent document selection
    setMenuOpen(null); // Close the menu
    setIsEditing(true);
    setSelectedDocument(documentName);
  };

  const handleDocumentClick = (documentName) => {
    setSelectedDocument(documentName); // Select the document when clicked
  };

  // Toggle menu open/closed for a document
  const toggleMenu = (event, documentName) => {
    event.stopPropagation(); // Prevent document selection
    setMenuOpen(menuOpen === documentName ? null : documentName);
  };

  // Toggle star status for a document
  const toggleStar = (event, documentName) => {
    event.stopPropagation(); // Prevent document selection
    
    setStarredDocs(prev => ({
      ...prev,
      [documentName]: !prev[documentName]
    }));
  };

  // Start renaming a document
  const startRename = (event, documentName) => {
    event.stopPropagation(); // Prevent document selection
    setMenuOpen(null); // Close the menu
    setRenaming(documentName);
    setNewName(documentName); // Pre-fill with current name
  };

  // Handle rename input changes
  const handleRenameChange = (e) => {
    setNewName(e.target.value);
  };

  // Handle rename form submission
  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    
    if (!newName.trim() || newName === renaming) {
      setRenaming(null);
      return;
    }

    try {
      // Get the document path
      const docToRename = documents.find(doc => doc.name === renaming);
      
      if (!docToRename) {
        console.error("Document not found");
        return;
      }

      // Send a POST request to rename the document
      const response = await fetch(`${API_BASE_URL}/backend/api/Documents/renameDocument.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          oldName: renaming,
          newName: newName,
          filePath: docToRename.resume_path
        }),
      });

      const result = await response.json();

      if (result.status === "success") {
        // Update the documents list with the new name
        setDocuments(prevDocs => 
          prevDocs.map(doc => 
            doc.name === renaming ? { ...doc, name: newName, resume_path: result.newPath } : doc
          )
        );

        // Update selected document if it was the renamed one
        setSelectedDocument(prevSelected => 
          prevSelected === renaming ? newName : prevSelected
        );
        
        // Update starred status if document was starred
        if (starredDocs[renaming]) {
          const newStarredDocs = {...starredDocs};
          delete newStarredDocs[renaming];
          newStarredDocs[newName] = true;
          setStarredDocs(newStarredDocs);
        }
      } else {
        console.error("Failed to rename document:", result.message);
      }
    } catch (error) {
      console.error("Error renaming document:", error);
    }

    // Reset renaming state
    setRenaming(null);
  };

  // Handle cancel renaming
  const handleRenameCancel = () => {
    setRenaming(null);
  };

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = () => {
      setMenuOpen(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div className="expanded-sidebar">
      <input type="file" id="fileInput" onChange={handleAddDocument} style={{ display: "none" }} />
      <label htmlFor="fileInput" className="add-document-button">+ Add Document</label>
      <ul>
        {documents.length === 0 ? (
          <p style={{ color: "black" }}>
            No documents available. Upload a .DOCX file to get started.
          </p>
        ) : (
          documents.map((doc, index) => (
            <li key={index} className="clickable-document">
              <span 
                className="document-icon" 
                onClick={() => handleDocumentClick(doc.name)}
              >
                📄
              </span>
              
              {renaming === doc.name ? (
                // Rename input form
                <form onSubmit={handleRenameSubmit} className="rename-form">
                  <input
                    type="text"
                    value={newName}
                    onChange={handleRenameChange}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    className="rename-input"
                  />
                  <button type="submit" className="rename-save-btn">✓</button>
                  <button type="button" onClick={handleRenameCancel} className="rename-cancel-btn">✕</button>
                </form>
              ) : (
                // Normal document display
                <>
                  <span className="document-name" onClick={() => handleDocumentClick(doc.name)}>
                    {doc.name}
                  </span>
                  <span 
                    className="star-icon" 
                    onClick={(event) => toggleStar(event, doc.name)}
                  >
                    {starredDocs[doc.name] ? '★' : '☆'}
                  </span>
                  <span 
                    className="menu-icon" 
                    onClick={(event) => toggleMenu(event, doc.name)}
                  >
                    ⋮
                  </span>
                  {menuOpen === doc.name && (
                    <div className="document-menu">
                      <div className="menu-item" onClick={(event) => startRename(event, doc.name)}>
                        ✏️ Rename
                      </div>
                      <div className="menu-item" onClick={(event) => handleEdit(event, doc.name)}>
                        ✏️ Edit
                      </div>
                      <div className="menu-item" onClick={(event) => handleDelete(event, doc.name)}>
                        🗑️ Delete
                      </div>
                    </div>
                  )}
                </>
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export default ExpandedSideBar;